import { apiEndpoints } from '@/services/api';
import { store } from '@/store';

interface QueuedMutation {
  id: string;
  endpoint: string;
  args: any;
  timestamp: number;
  retryCount: number;
}

const QUEUE_KEY = 'wsapi-offline-queue';
const MAX_RETRY_COUNT = 3;

class OfflineQueue {
  private queue: QueuedMutation[] = [];
  private isProcessing = false;
  private isOnline = navigator.onLine;

  constructor() {
    this.loadQueue();
    this.setupEventListeners();
    if (this.isOnline && this.queue.length > 0) {
      this.processQueue();
    }
  }

  private setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      if (this.queue.length > 0) {
        this.processQueue();
      }
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private loadQueue() {
    try {
      const saved = localStorage.getItem(QUEUE_KEY);
      if (saved) {
        this.queue = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load offline queue:', error);
      this.queue = [];
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save offline queue:', error);
    }
  }

  public addToQueue(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>) {
    const queuedMutation: QueuedMutation = {
      ...mutation,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(queuedMutation);
    this.saveQueue();

    // Try to process immediately if online
    if (this.isOnline && !this.isProcessing) {
      this.processQueue();
    }
  }

  private async processQueue() {
    if (this.isProcessing || !this.isOnline || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const processedIds: string[] = [];

    for (const mutation of [...this.queue]) {
      try {
        // Get the endpoint function
        const endpoint = (apiEndpoints.endpoints as any)[mutation.endpoint];
        if (!endpoint) {
          console.error(`Endpoint ${mutation.endpoint} not found`);
          processedIds.push(mutation.id);
          continue;
        }

        // Dispatch the mutation
        const result = await store.dispatch(
          endpoint.initiate(mutation.args)
        );

        if ('error' in result) {
          throw result.error;
        }

        // Success - remove from queue
        processedIds.push(mutation.id);
      } catch (error) {
        console.error(`Failed to process queued mutation ${mutation.id}:`, error);
        
        // Increment retry count
        mutation.retryCount++;
        
        // Remove if max retries exceeded
        if (mutation.retryCount >= MAX_RETRY_COUNT) {
          processedIds.push(mutation.id);
          console.error(`Max retries exceeded for mutation ${mutation.id}`);
        }
      }
    }

    // Remove processed mutations
    this.queue = this.queue.filter(m => !processedIds.includes(m.id));
    this.saveQueue();

    this.isProcessing = false;

    // Process remaining mutations if any
    if (this.queue.length > 0 && this.isOnline) {
      setTimeout(() => this.processQueue(), 5000);
    }
  }

  public getQueuedMutations(): QueuedMutation[] {
    return [...this.queue];
  }

  public clearQueue() {
    this.queue = [];
    this.saveQueue();
  }

  public removeFromQueue(id: string) {
    this.queue = this.queue.filter(m => m.id !== id);
    this.saveQueue();
  }
}

// Create singleton instance
export const offlineQueue = new OfflineQueue();

// Hook for components to use
export function useOfflineQueue() {
  return {
    addToQueue: (mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>) =>
      offlineQueue.addToQueue(mutation),
    getQueuedMutations: () => offlineQueue.getQueuedMutations(),
    clearQueue: () => offlineQueue.clearQueue(),
    removeFromQueue: (id: string) => offlineQueue.removeFromQueue(id),
  };
}